// src/components/RolePermissionManager.jsx
// หน้าจัดการสิทธิ์เมนูตาม Role
// Layout: ซ้าย = role list, ขวา = checkbox matrix ของเมนู
import React, { useEffect, useState, useCallback } from "react";
import Button1   from "@material-ui/core/Button";
import Grid      from "@material-ui/core/Grid";
import Container from "@material-ui/core/Container";
import { useStyles } from "../../Styles.js";
import { useAuth }   from "../../hooks/useAuth";
import { Toast }         from "../Menu/Toast";  

// ── Build menu tree ────────────────────────────────────────────────────────────
function buildTree(menus) {
  const map   = {};
  menus.forEach(m => { map[m.MENU_ID] = { ...m, children: [] }; });
  const roots = [];
  menus.forEach(m => {
    if (m.MENU_PARENT_ID && map[m.MENU_PARENT_ID]) {
      map[m.MENU_PARENT_ID].children.push(map[m.MENU_ID]);
    } else {
      roots.push(map[m.MENU_ID]);
    }
  });
  const sort = nodes => {
    nodes.sort((a, b) => a.MENU_SEQ - b.MENU_SEQ);
    nodes.forEach(n => sort(n.children));
    return nodes;
  };
  return sort(roots);
}

// ── Role list sidebar ──────────────────────────────────────────────────────────
function RoleList({ roles, selectedId, onSelect }) {
  return (
    <div style={{
      width: 240, flexShrink: 0, borderRight: "1px solid #e2e8f0",
      display: "flex", flexDirection: "column", background: "#fff",
    }}>
      <div style={{ padding: "12px 14px", borderBottom: "1px solid #e2e8f0" }}>
        <span style={{ fontWeight: 700, fontSize: 13, color: "#1e293b" }}>User Roles</span>
      </div>
      <div style={{ flex: 1, overflowY: "auto", padding: "6px 0" }}>
        {roles.map(r => {
          const isActive = selectedId === r.ROLE_ID;
          return (
            <div
              key={r.ROLE_ID}
              onClick={() => onSelect(r.ROLE_ID)}
              style={{
                padding:    "10px 14px",
                cursor:     "pointer",
                borderLeft: isActive ? "3px solid #1d4ed8" : "3px solid transparent",
                background: isActive ? "#eff6ff" : "transparent",
                transition: "background 0.1s",
              }}
              onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = "#f8fafc"; }}
              onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = "transparent"; }}
            >
              <div style={{ fontSize: 13, fontWeight: isActive ? 700 : 500, color: isActive ? "#1d4ed8" : "#1e293b" }}>
                {r.ROLE_NAME}
              </div>
              {r.ROLE_DESC && (
                <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>{r.ROLE_DESC}</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Menu permission row ────────────────────────────────────────────────────────
function MenuPermRow({ menu, depth, checked, canEdit, onToggleView, onToggleEdit }) {
  return (
    <div style={{
      display:      "flex",
      alignItems:   "center",
      gap:          8,
      padding:      "8px 12px",
      paddingLeft:  depth * 24 + 12,
      borderBottom: "1px solid #f8fafc",
      background:   depth === 0 ? "#fafafa" : "#fff",
    }}>
      {/* Icon + name */}
      <i
        className={`ti ${menu.MENU_ICON || "ti-square"}`}
        style={{ fontSize: 15, color: "#64748b", width: 18, flexShrink: 0 }}
      />
      <span style={{
        flex: 1, fontSize: 13,
        fontWeight: depth === 0 ? 600 : 400,
        color: "#1e293b",
      }}>
        {menu.MENU_TEXT}
      </span>
      <span style={{ fontSize: 11, color: "#94a3b8", fontFamily: "monospace", width: 160, textAlign: "right", flexShrink: 0 }}>
        {menu.MENU_URL || ""}
      </span>

      {/* View checkbox */}
      <label style={{
        display: "flex", alignItems: "center", gap: 5,
        cursor: "pointer", userSelect: "none", flexShrink: 0, width: 70, justifyContent: "center",
      }}>
        <input
          type="checkbox"
          checked={checked}
          onChange={onToggleView}
          style={{ width: 15, height: 15, cursor: "pointer", accentColor: "#1d4ed8" }}
        />
        <span style={{ fontSize: 11, color: checked ? "#1d4ed8" : "#94a3b8", fontWeight: 600 }}>View</span>
      </label>

      {/* Edit checkbox — enabled only if View is checked */}
      <label style={{
        display: "flex", alignItems: "center", gap: 5,
        cursor: checked ? "pointer" : "default", userSelect: "none",
        flexShrink: 0, width: 70, justifyContent: "center",
        opacity: checked ? 1 : 0.3,
      }}>
        <input
          type="checkbox"
          checked={canEdit && checked}
          disabled={!checked}
          onChange={onToggleEdit}
          style={{ width: 15, height: 15, cursor: checked ? "pointer" : "default", accentColor: "#f59e0b" }}
        />
        <span style={{ fontSize: 11, color: canEdit && checked ? "#d97706" : "#94a3b8", fontWeight: 600 }}>Edit</span>
      </label>
    </div>
  );
}

function MenuPermTree({ nodes, depth, permMap, onToggleView, onToggleEdit }) {
  return nodes.map(node => (
    <React.Fragment key={node.MENU_ID}>
      <MenuPermRow
        menu={node}
        depth={depth}
        checked={!!permMap[node.MENU_ID]}
        canEdit={permMap[node.MENU_ID]?.CAN_EDIT ?? false}
        onToggleView={() => onToggleView(node.MENU_ID)}
        onToggleEdit={() => onToggleEdit(node.MENU_ID)}
      />
      {node.children.length > 0 && (
        <MenuPermTree
          nodes={node.children}
          depth={depth + 1}
          permMap={permMap}
          onToggleView={onToggleView}
          onToggleEdit={onToggleEdit}
        />
      )}
    </React.Fragment>
  ));
}

// ── Main component ─────────────────────────────────────────────────────────────
export function RolePermissionManager() {
  const classes = useStyles();
  const {
    roles, permissions, allMenus,
    loading, error, clearError,
    fetchRoles, fetchPermissionsByRole, fetchAllMenus,
    bulkSave,
  } = useAuth();

  const [selectedRoleId, setSelectedRoleId] = useState(null);
  const [permMap,        setPermMap]         = useState({});  // { MENU_ID: { CAN_EDIT: bool } }
  const [dirty,          setDirty]           = useState(false);
  const [saving,         setSaving]          = useState(false);
  const [toast,          setToast]           = useState(null);

  const showToast = useCallback((msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  }, []);

  // โหลด roles + all menus ครั้งแรก
  useEffect(() => {
    fetchRoles();
    fetchAllMenus();
  }, [fetchRoles, fetchAllMenus]);

  // เมื่อเลือก role ใหม่ โหลด permissions
  useEffect(() => {
    if (selectedRoleId === null) return;
    fetchPermissionsByRole(selectedRoleId).then(() => setDirty(false));
  }, [selectedRoleId, fetchPermissionsByRole]);

  // sync permissions → permMap
  useEffect(() => {
    const map = {};
    permissions.forEach(p => {
      map[p.MENU_ID] = { CAN_EDIT: p.CAN_EDIT };
    });
    setPermMap(map);
    setDirty(false);
  }, [permissions]);

  const handleSelectRole = (roleId) => {
    if (dirty) {
      if (!window.confirm("มีการเปลี่ยนแปลงที่ยังไม่บันทึก ต้องการออกจาก role นี้?")) return;
    }
    setSelectedRoleId(roleId);
  };

  const handleToggleView = (menuId) => {
    setPermMap(prev => {
      const next = { ...prev };
      if (next[menuId]) {
        delete next[menuId];  // uncheck view → ลบออก
      } else {
        next[menuId] = { CAN_EDIT: false };
      }
      return next;
    });
    setDirty(true);
  };

  const handleToggleEdit = (menuId) => {
    setPermMap(prev => ({
      ...prev,
      [menuId]: { CAN_EDIT: !prev[menuId]?.CAN_EDIT },
    }));
    setDirty(true);
  };

  // Select All / Deselect All
  const handleSelectAll = () => {
    const map = {};
    allMenus.forEach(m => { map[m.MENU_ID] = { CAN_EDIT: false }; });
    setPermMap(map);
    setDirty(true);
  };
  const handleDeselectAll = () => {
    setPermMap({});
    setDirty(true);
  };

  const handleSave = async () => {
    if (!selectedRoleId) return;
    setSaving(true);
    try {
      const perms = Object.entries(permMap).map(([menuId, v]) => ({
        MENU_ID:  parseInt(menuId),
        CAN_EDIT: v.CAN_EDIT,
      }));
      await bulkSave(selectedRoleId, perms);
      setDirty(false);
      showToast(`✅ บันทึกสิทธิ์ ${perms.length} เมนู สำเร็จ`);
    } catch (e) {
      showToast("❌ " + e.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const selectedRole = roles.find(r => r.ROLE_ID === selectedRoleId);
  const menuTree     = buildTree(allMenus);
  const checkedCount = Object.keys(permMap).length;
  const editCount    = Object.values(permMap).filter(v => v.CAN_EDIT).length;

  return (
    <Container maxWidth="xl">
      <Toast toast={toast} />

      <Grid container spacing={2}>
        <Grid item xs={12}>&nbsp;</Grid>
      </Grid>

      {/* Page title */}
      <Grid container spacing={2}>
        <Grid item xs={12} sm={10}>
          <label className="head">Role Permission Management</label>
        </Grid>
      </Grid>

      {/* Action bar */}
      <Grid container spacing={2}>
        <Grid item xs={12} style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
          <Button1
            className="searchButton"
            variant="contained"
            color="primary"
            onClick={() => { fetchRoles(); fetchAllMenus(); }}
            disabled={loading}
          >
            {loading ? "⏳ Loading..." : "Refresh"}
          </Button1>

          {selectedRoleId && (
            <>
              <div style={{ width: 1, height: 28, background: "#e2e8f0" }} />

              <Button1
                variant="outlined" size="small"
                onClick={handleSelectAll}
                style={{ textTransform: "none", fontSize: 12 }}
              >
                ✅ เลือกทั้งหมด
              </Button1>

              <Button1
                variant="outlined" size="small"
                onClick={handleDeselectAll}
                style={{ textTransform: "none", fontSize: 12 }}
              >
                ☐ ล้างทั้งหมด
              </Button1>

              <div style={{ width: 1, height: 28, background: "#e2e8f0" }} />

              <Button1
                variant="contained"
                onClick={handleSave}
                disabled={saving || !dirty}
                style={{
                  background:    saving || !dirty ? "#e2e8f0" : "#16a34a",
                  color:         saving || !dirty ? "#94a3b8" : "#fff",
                  fontWeight:    700, textTransform: "none", fontSize: 13, boxShadow: "none",
                }}
              >
                {saving ? "⏳ กำลังบันทึก..." : `💾 บันทึกสิทธิ์ (${checkedCount} เมนู)`}
              </Button1>

              {dirty && (
                <span style={{
                  fontSize: 11, fontWeight: 600,
                  background: "#fffbeb", color: "#d97706",
                  border: "1px solid #fde68a", borderRadius: 6, padding: "3px 10px",
                }}>
                  ⚠️ มีการเปลี่ยนแปลงที่ยังไม่บันทึก
                </span>
              )}
            </>
          )}

          {error && (
            <span onClick={clearError} style={{
              background: "#fef2f2", color: "#dc2626",
              border: "1px solid #fca5a5", borderRadius: 6,
              padding: "4px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer",
            }}>
              ❌ {error} (คลิกเพื่อปิด)
            </span>
          )}
        </Grid>
      </Grid>

      {/* Main layout */}
      <Grid container className={classes.root} style={{ marginTop: 12 }}>
        <div style={{
          display: "flex", width: "100%",
          border: "1px solid #e2e8f0", borderRadius: 8,
          overflow: "hidden", minHeight: 600, background: "#fff",
        }}>

          {/* Left: Role list */}
          <RoleList
            roles={roles}
            selectedId={selectedRoleId}
            onSelect={handleSelectRole}
          />

          {/* Right: Permission editor */}
          <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>

            {!selectedRoleId ? (
              <div style={{
                flex: 1, display: "flex", flexDirection: "column",
                alignItems: "center", justifyContent: "center",
                gap: 10, color: "#94a3b8",
              }}>
                <i className="ti ti-shield" style={{ fontSize: 44, opacity: 0.2 }} />
                <p style={{ fontSize: 13 }}>เลือก Role ด้านซ้ายเพื่อจัดการสิทธิ์เมนู</p>
              </div>
            ) : (
              <>
                {/* Header */}
                <div style={{
                  padding: "12px 16px", borderBottom: "1px solid #e2e8f0",
                  display: "flex", alignItems: "center", gap: 12, flexShrink: 0,
                }}>
                  <div>
                    <span style={{ fontSize: 14, fontWeight: 700, color: "#1e293b" }}>
                      🔐 {selectedRole?.ROLE_NAME}
                    </span>
                    {selectedRole?.ROLE_DESC && (
                      <span style={{ fontSize: 12, color: "#64748b", marginLeft: 8 }}>
                        — {selectedRole.ROLE_DESC}
                      </span>
                    )}
                  </div>
                  <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
                    <span style={{
                      fontSize: 11, padding: "2px 8px", borderRadius: 10, fontWeight: 700,
                      background: "#eff6ff", color: "#1d4ed8",
                    }}>
                      View: {checkedCount} เมนู
                    </span>
                    <span style={{
                      fontSize: 11, padding: "2px 8px", borderRadius: 10, fontWeight: 700,
                      background: "#fffbeb", color: "#d97706",
                    }}>
                      Edit: {editCount} เมนู
                    </span>
                  </div>
                </div>

                {/* Column headers */}
                <div style={{
                  display: "flex", gap: 8, padding: "7px 12px",
                  background: "#f1f5f9", borderBottom: "1px solid #e2e8f0",
                  flexShrink: 0,
                }}>
                  <span style={{ flex: 1, fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase" }}>Menu</span>
                  <span style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", width: 160 }}>URL</span>
                  <span style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", width: 70, textAlign: "center" }}>View</span>
                  <span style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", width: 70, textAlign: "center" }}>Edit</span>
                </div>

                {/* Menu tree with checkboxes */}
                <div style={{ flex: 1, overflowY: "auto" }}>
                  {loading ? (
                    <div style={{ padding: 32, textAlign: "center", color: "#64748b", fontSize: 13 }}>
                      ⏳ กำลังโหลด...
                    </div>
                  ) : (
                    <MenuPermTree
                      nodes={menuTree}
                      depth={0}
                      permMap={permMap}
                      onToggleView={handleToggleView}
                      onToggleEdit={handleToggleEdit}
                    />
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </Grid>
    </Container>
  );
}
